// src/pages/tournament/RequestsEditor.jsx
// Pairings the players asked for, for the next round: partners who want to play together (against chosen opponents or against
// opponents the planner picks), and singles games (against a chosen opponent, or one picked for them). They are granted first
// and the rest of the round is planned around them. Cleared when the round has been made.
import React, { useState } from 'react';
import { ChevronIcon, PlusIcon, TrashIcon } from '../../components/icons';

const nameOf = (players, id) => {
    const player = players.find((candidate) => candidate.playerId === id);
    return player ? `${player.firstName} ${player.lastName}`.trim() : `#${id}`;
};

// How a request reads: "Ada & Bob vs Cy & Di", "Ada & Bob (opponents chosen for them)", "Ada vs Bob (singles)".
export function describeRequest(request, players) {
    const who = (ids) => ids.map((id) => nameOf(players, id)).join(' & ');
    if (request.singles) {
        const [first, second] = request.singles;
        return second === null ? `${who([first])} (singles, opponent chosen for them)` : `${who([first])} vs ${who([second])} (singles)`;
    }
    return request.team2 ? `${who(request.team1)} vs ${who(request.team2)}` : `${who(request.team1)} (opponents chosen for them)`;
}

export const usedIds = (requests) =>
    requests.flatMap((request) => (request.singles ? request.singles : [...request.team1, ...(request.team2 || [])])).filter((id) => id !== null);

function PlayerSelect({ label, value, onChange, options, placeholder = 'Choose…' }) {
    return (
        <select aria-label={label} value={value ?? ''} onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}>
            <option value="">{placeholder}</option>
            {options.map((player) => (
                <option key={player.playerId} value={player.playerId}>
                    {player.firstName} {player.lastName}
                </option>
            ))}
        </select>
    );
}

function RequestsEditor({ players, requests, onChange, courts }) {
    const [open, setOpen] = useState(false);
    const [kind, setKind] = useState('doubles'); // 'doubles' | 'singles'
    const [chooseOpponents, setChooseOpponents] = useState(false);
    const [picks, setPicks] = useState({ a1: null, a2: null, b1: null, b2: null });
    const active = players.filter((player) => player.active !== false);
    const taken = new Set(usedIds(requests));
    const chosenNow = new Set(Object.values(picks).filter((id) => id !== null));
    const optionsFor = (own) => active.filter((player) => !taken.has(player.playerId) && (!chosenNow.has(player.playerId) || player.playerId === own));

    const set = (key) => (value) => setPicks((current) => ({ ...current, [key]: value }));
    const singles = kind === 'singles';
    const complete = singles
        ? picks.a1 !== null && (picks.b1 !== null || !chooseOpponents)
        : picks.a1 !== null && picks.a2 !== null && (!chooseOpponents || (picks.b1 !== null && picks.b2 !== null));

    const draft = complete
        ? singles
            ? { singles: [picks.a1, chooseOpponents ? picks.b1 : null] }
            : { team1: [picks.a1, picks.a2], team2: chooseOpponents ? [picks.b1, picks.b2] : null }
        : null;
    const started = chosenNow.size > 0;
    const [justSaved, setJustSaved] = useState(false);

    const add = () => {
        onChange([...requests, draft]);
        setPicks({ a1: null, a2: null, b1: null, b2: null });
        setJustSaved(true);
    };

    const courtsNeeded = requests.length;
    const tooMany = Boolean(courts) && courtsNeeded > courts;

    return (
        <section className="card requests-card" aria-label="Requests for the next round">
            <button className="section-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
                <ChevronIcon open={open} />
                <h3>Requests for the next round</h3>
                {requests.length > 0 && <span className="badge badge-primary">{requests.length}</span>}
            </button>
            {open && (
                <div className="requests-body">
                    <p className="hint">
                        Players who want to play together, or against certain others. Requests are granted first; everybody else is
                        paired as usual. The players in a request always play this round.
                    </p>

                    {requests.length > 0 && (
                        <>
                        <p className="hint" id="saved-requests">Saved requests (used when you create the round):</p>
                        <ul className="request-list" aria-label="Requests">
                            {requests.map((request, number) => (
                                <li key={number}>
                                    <span>{describeRequest(request, players)}</span>
                                    <button
                                        className="btn btn-ghost btn-icon btn-icon-danger btn-sm"
                                        aria-label={`Remove request: ${describeRequest(request, players)}`}
                                        onClick={() => onChange(requests.filter((_, index) => index !== number))}
                                    >
                                        <TrashIcon size={16} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                        </>
                    )}
                    {tooMany && (
                        <p className="form-error" role="alert">
                            {courtsNeeded} requests need {courtsNeeded} courts, and there are {courts}.
                        </p>
                    )}

                    <fieldset className="request-form">
                        <legend>New request</legend>
                        <div className="request-kind" role="radiogroup" aria-label="Kind of request">
                            {[['doubles', 'Partners'], ['singles', 'Singles']].map(([key, label]) => (
                                <label key={key}>
                                    <input type="radio" name="request-kind" checked={kind === key} onChange={() => setKind(key)} /> {label}
                                </label>
                            ))}
                        </div>
                        <div className="request-picks">
                            <PlayerSelect label={singles ? 'Player' : 'Partner 1'} value={picks.a1} onChange={set('a1')} options={optionsFor(picks.a1)} />
                            {!singles && <PlayerSelect label="Partner 2" value={picks.a2} onChange={set('a2')} options={optionsFor(picks.a2)} />}
                        </div>
                        <label className="request-choose">
                            <input type="checkbox" checked={chooseOpponents} onChange={(event) => setChooseOpponents(event.target.checked)} />{' '}
                            {singles ? 'Choose the opponent (otherwise one is picked)' : 'Choose the opponents (otherwise they are picked)'}
                        </label>
                        {chooseOpponents && (
                            <div className="request-picks">
                                <PlayerSelect label={singles ? 'Opponent' : 'Opponent 1'} value={picks.b1} onChange={set('b1')} options={optionsFor(picks.b1)} />
                                {!singles && <PlayerSelect label="Opponent 2" value={picks.b2} onChange={set('b2')} options={optionsFor(picks.b2)} />}
                            </div>
                        )}
                        {draft && (
                            <p className="request-preview" aria-live="polite">
                                This request: <strong>{describeRequest(draft, players)}</strong>
                            </p>
                        )}
                        <button className="btn btn-sm btn-primary" type="button" onClick={add} disabled={!complete}>
                            <PlusIcon size={14} /> Save this request
                        </button>
                        {started && !complete && <p className="hint">Fill in every box, then save the request.</p>}
                        {justSaved && !started && (
                            <p className="hint" role="status">
                                Saved. It is used when you create the round; you can enter another one now.
                            </p>
                        )}
                    </fieldset>
                </div>
            )}
        </section>
    );
}

export default RequestsEditor;
